import React, { useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import ImageUploadField from '../../shared-components/ImageUploadField';
import { normalizeOptionalRank } from '../../../utils/promoRankValidation';

const AddShopItemForm = ({
  onAdd,
  token,
  isVip = false,
  isWaitlist = false,
  showRank = false,
  rankOptions = [],
}) => {
  // WAITLIST items are granted by the wave close, never shop-purchased —
  // the same locked shape as VIP (Venom's re-review, 2026-08-25: the ADD
  // path used to drop the flag, so a WAITLIST promo could not use the item
  // editor coherently).
  const granted = isVip || isWaitlist;
  const grantSource = isVip ? 'the VIP cron' : 'the wave close';
  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    cost: 0,
    upperLimit: 0,
    dailyPurchaseLimit: 0,
    imageUrl: '',
    currency: 'FRAGMENTS',
    ungiftable: false,
    rank: '',
  });
  const [submitError, setSubmitError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = event => {
    const { name, value } = event.target;
    setFieldErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
    setFormValues(prevValues => ({
      ...prevValues,
      [name]:
        name === 'cost' || name === 'upperLimit' || name === 'dailyPurchaseLimit'
          ? isNaN(parseFloat(value))
            ? 0
            : parseFloat(value)
          : value,
    }));
  };

  const handleChangeUngiftable = () => {
    setFormValues(prevValues => ({
      ...prevValues,
      ungiftable: !formValues.ungiftable,
    }));
  };

  // Mirrors atlantis's createShopItem joi schema (name/description/imageUrl are
  // required NON-EMPTY strings; rank is our LEADERBOARD-required rule) so every
  // miss gets a field-level message instead of a swallowed 400.
  const validate = () => {
    const errors = {};
    if (!formValues.name.trim()) {
      errors.name = 'Name is required.';
    }
    if (!formValues.description.trim()) {
      errors.description = 'Description is required.';
    }
    if (!formValues.imageUrl) {
      errors.imageUrl = 'Item image is required — upload one before saving.';
    }
    // Leaderboard prize items MUST carry a rank: the rank-fit grant pays
    // prizes by item.rank, so an unranked item on a LEADERBOARD promo is a
    // prize nobody can ever win (Gwen 2026-07-07: do not allow unranked).
    if (showRank && normalizeOptionalRank(formValues.rank) === null) {
      errors.rank = 'Leaderboard items must have a rank — pick a place (1..N).';
    }
    return errors;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setSubmitError('');

    const errors = validate();
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) {
      setSubmitError('Please fix the highlighted fields below.');
      return;
    }

    setSubmitting(true);

    // Set discount cost equal to cost before submitting
    // VIP rewards are granted free by the cron — cost is always 0, never editable.
    const cost = granted ? 0 : formValues.cost;
    const submitValues = {
      ...formValues,
      cost,
      ogCost: cost, // Keep ogCost for API compatibility
    };
    if (granted) {
      // Granted items (VIP cron / waitlist wave close) are never
      // shop-purchased: inventory caps are inert for them, and a direct
      // (rewardless) item can't be claimed — force the safe shape even if
      // the locked inputs are bypassed.
      submitValues.upperLimit = 0;
      submitValues.dailyPurchaseLimit = 0;
      submitValues.ungiftable = false;
    }
    if (showRank) {
      submitValues.rank = normalizeOptionalRank(formValues.rank);
    } else {
      delete submitValues.rank;
    }

    try {
      const result = await onAdd(submitValues);
      if (result?.success === false) {
        setSubmitError(result.error || 'Failed to add shop item.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to render form fields
  const renderFormField = (id, label, type = 'text', required = false, disabled = false) => (
    <div className="mb-4">
      <label
        htmlFor={id}
        className="form-label text-muted fw-medium mb-2"
        style={{ fontSize: '0.875rem' }}
      >
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      <input
        type={type}
        className="form-control form-control-lg shadow-none border-light-subtle rounded-3"
        id={id}
        name={id}
        value={formValues[id]}
        onChange={handleChange}
        required={required}
        disabled={disabled}
        style={{
          padding: '0.75rem 1rem',
          fontSize: '1rem',
          transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
        }}
      />
      {fieldErrors[id] && (
        <div className="text-danger mt-2" style={{ fontSize: '0.8rem' }}>
          {fieldErrors[id]}
        </div>
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white p-4 rounded-4 shadow-sm">
      <h5 className="mb-4 fw-semibold text-dark">Add New {granted ? 'Reward' : 'Shop'} Item</h5>
      {submitError && (
        <Alert variant="danger" className="mb-4">
          {submitError}
        </Alert>
      )}

      <div className="row g-4">
        <div className="col-md-6">
          <div className="card border-0 shadow-sm rounded-4 mb-4">
            <div className="card-body">
              <h6 className="card-subtitle mb-3 text-muted fw-medium">Basic Information</h6>
              {renderFormField('name', 'Name', 'text', true)}

              <div className="mb-4">
                <label
                  htmlFor="description"
                  className="form-label text-muted fw-medium mb-2"
                  style={{ fontSize: '0.875rem' }}
                >
                  Description<span className="text-danger ms-1">*</span>
                </label>
                <textarea
                  className="form-control shadow-none border-light-subtle rounded-3"
                  id="description"
                  name="description"
                  value={formValues.description}
                  onChange={handleChange}
                  rows="4"
                  required
                  style={{
                    padding: '0.75rem 1rem',
                    fontSize: '1rem',
                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                  }}
                />
                {fieldErrors.description && (
                  <div className="text-danger mt-2" style={{ fontSize: '0.8rem' }}>
                    {fieldErrors.description}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm rounded-4 mb-4">
            <div className="card-body">
              <h6 className="card-subtitle mb-3 text-muted fw-medium">Item Type</h6>
              <div className="mb-4">
                {isWaitlist ? (
                  <>
                    <label
                      className="form-label text-muted fw-medium mb-2"
                      style={{ fontSize: '0.875rem' }}
                    >
                      Item Behavior
                    </label>
                    <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Granted prize box with rewards — winners open it later.
                    </div>
                  </>
                ) : (
                  <>
                    <label
                      htmlFor="ungiftable"
                      className="form-label text-muted fw-medium mb-2"
                      style={{ fontSize: '0.875rem' }}
                    >
                      Item Behavior
                    </label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        name="ungiftable"
                        id="ungiftable"
                        checked={!granted && formValues.ungiftable === true}
                        onChange={handleChangeUngiftable}
                        disabled={granted}
                        style={{ width: '2.5em', height: '1.25em' }}
                      />
                      <label className="form-check-label" htmlFor="ungiftable">
                        &nbsp;Direct Item (No Rewards)
                      </label>
                    </div>
                    <small className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
                      {granted
                        ? `Locked — these rewards are claimed by opening a granted box, so a direct item could never be claimed.`
                        : 'Toggle ON if this is a direct item that cannot be opened. Toggle OFF if this is a box that reveals rewards when opened.'}
                    </small>
                  </>
                )}
              </div>

              <div className="mb-4">
                <label
                  className="form-label text-muted fw-medium mb-2"
                  style={{ fontSize: '0.875rem' }}
                >
                  Item Status
                </label>
                {/* The server creates every item disabled regardless of what the
                    form sends (createShopItem passes a literal `true`), so a
                    toggle here would be a lie — state the behavior instead. */}
                <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                  Items are always created disabled. Enable the item from its item page once it is
                  fully configured.
                </div>
              </div>
            </div>
          </div>

          {showRank && (
            <div className="card border-0 shadow-sm rounded-4 mb-4">
              <div className="card-body">
                <h6 className="card-subtitle mb-3 text-muted fw-medium">Leaderboard Settings</h6>
                <Form.Group className="mb-4" controlId="rank">
                  <Form.Label
                    className="text-muted fw-medium mb-2"
                    style={{ fontSize: '0.875rem' }}
                  >
                    Leaderboard Rank
                  </Form.Label>
                  <Form.Select
                    name="rank"
                    value={formValues.rank}
                    onChange={handleChange}
                    className="form-control-lg shadow-none border-light-subtle rounded-3"
                  >
                    <option value="" disabled>
                      Select rank…
                    </option>
                    {rankOptions.map(rank => (
                      <option key={rank} value={rank}>
                        {rank}
                      </option>
                    ))}
                  </Form.Select>
                  <small className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
                    Required. Ranks must be distinct and contiguous from 1 (1 = 🥇).
                  </small>
                  {fieldErrors.rank && (
                    <div className="text-danger mt-2" style={{ fontSize: '0.8rem' }}>
                      {fieldErrors.rank}
                    </div>
                  )}
                </Form.Group>
              </div>
            </div>
          )}

          <div className="card border-0 shadow-sm rounded-4 mb-4">
            <div className="card-body">
              <h6 className="card-subtitle mb-3 text-muted fw-medium">Pricing</h6>
              <div className="mb-4">
                <label
                  htmlFor="cost"
                  className="form-label text-muted fw-medium mb-2"
                  style={{ fontSize: '0.875rem' }}
                >
                  Cost (in Fragments)
                  {!granted && <span className="text-danger ms-1">*</span>}
                </label>
                <input
                  type="number"
                  className="form-control form-control-lg shadow-none border-light-subtle rounded-3"
                  id="cost"
                  name="cost"
                  value={granted ? 0 : formValues.cost}
                  onChange={handleChange}
                  required={!granted}
                  disabled={granted}
                  style={{
                    padding: '0.75rem 1rem',
                    fontSize: '1rem',
                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                  }}
                />
              </div>
              <small className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
                {granted
                  ? `Granted free by ${grantSource} — cost is always 0.`
                  : 'The cost in Fragments currency that users will pay for this item.'}
              </small>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card border-0 shadow-sm rounded-4 mb-4">
            <div className="card-body">
              <h6 className="card-subtitle mb-3 text-muted fw-medium">
                Item Image<span className="text-danger ms-1">*</span>
              </h6>

              <ImageUploadField
                value={formValues.imageUrl}
                onChange={url => {
                  setFormValues(prev => ({ ...prev, imageUrl: url }));
                  if (url) setFieldErrors(prev => ({ ...prev, imageUrl: undefined }));
                }}
                token={token}
                id="add-shop-item-image"
              />
              {fieldErrors.imageUrl && (
                <div className="text-danger mt-2" style={{ fontSize: '0.8rem' }}>
                  {fieldErrors.imageUrl}
                </div>
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <h6 className="card-subtitle mb-3 text-muted fw-medium">Inventory Settings</h6>
              <div className="row g-3">
                <div className="col-md-6">
                  {renderFormField('upperLimit', 'Total Inventory', 'number', false, granted)}
                  <small className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
                    {granted
                      ? `Locked at unlimited — these rewards are granted by ${grantSource}, never sold from the shop.`
                      : 'Maximum number of items available (0 = unlimited)'}
                  </small>
                </div>
                <div className="col-md-6">
                  {renderFormField(
                    'dailyPurchaseLimit',
                    'Daily Purchase Limit',
                    'number',
                    false,
                    granted
                  )}
                  <small className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
                    {granted
                      ? 'Locked — granted items cannot be purchased, so a daily limit never applies.'
                      : 'Maximum purchases per user every 24 hours (0 = unlimited)'}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4 pt-3 border-top">
        <button
          type="submit"
          className="btn btn-primary rounded-3 px-4 py-2"
          disabled={submitting}
          style={{
            fontSize: '0.95rem',
            backgroundColor: '#6366f1',
            borderColor: '#6366f1',
            boxShadow: '0 2px 4px rgba(99, 102, 241, 0.25)',
          }}
        >
          {submitting ? 'ADDING...' : 'ADD ITEM'}
        </button>
      </div>
    </form>
  );
};

export default AddShopItemForm;
